import type { ReactNode } from 'react'
import type { ContextCardRef } from '@omni/shell-context'
import {
  asContactId,
  asCustomerId,
  asProjectId,
  asTaskId,
  asThreadId,
  type ContextCardResult,
  type ContextRef,
} from '@omni/objects'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { MailApiGet } from './mail-api'

/**
 * A resolved peek card. crm/projects speak the @omni/objects contract
 * (async, sections/rows); mail's provider predates it and returns a
 * synchronous { supported, render() } — the drawer renders both.
 */
export type PeekCard =
  | { type: 'sections'; app: string; card: ContextCardResult }
  | { type: 'node'; app: string; node: ReactNode }
  | { type: 'empty'; reason: string }

function toContextRef(ref: ContextCardRef): ContextRef {
  switch (ref.kind) {
    case 'customer':
      return { kind: 'customer', id: asCustomerId(ref.id) }
    case 'contact':
      return { kind: 'contact', id: asContactId(ref.id), namespace: ref.namespace }
    case 'thread':
      return { kind: 'thread', id: asThreadId(ref.id) }
    case 'task':
      return { kind: 'task', id: asTaskId(ref.id) }
    case 'project':
      return { kind: 'project', id: asProjectId(ref.id) }
  }
}

// Dispatch per meta.contextCardSupport (Correction 3): crm answers
// customer + contact(crm); projects answers customer + task + project; mail
// answers thread + contact(mail). Candidates are tried in order until one
// returns a card.
export async function loadPeekCard(
  ref: ContextCardRef,
  mailApiGet: MailApiGet | null,
  tenantClient: SupabaseClient,
): Promise<PeekCard> {
  const wantsMail =
    ref.kind === 'thread' || (ref.kind === 'contact' && ref.namespace === 'mail')

  if (wantsMail) {
    if (!mailApiGet) {
      return { type: 'empty', reason: 'Mail API is not configured (VITE_API_BASE_URL).' }
    }
    try {
      const { makeContextCardProvider } = await import(
        '@omni-command/mail/src/federation/contextCardProvider'
      )
      const provider = makeContextCardProvider({ apiGet: mailApiGet })
      const result = provider({
        kind: ref.kind as 'thread' | 'contact',
        id: ref.id,
        namespace: 'mail',
      })
      if (result.supported) return { type: 'node', app: 'mail', node: result.render() }
      return { type: 'empty', reason: 'Mail could not resolve this reference.' }
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[peek] mail provider failed', err)
      return { type: 'empty', reason: 'Mail context card failed to load.' }
    }
  }

  const contextRef = toContextRef(ref)
  const candidates: Array<{
    app: string
    load: () => Promise<{
      makeContextCardProvider: (client: SupabaseClient) => (r: ContextRef) => Promise<ContextCardResult | null>
    }>
  }> = []

  if (ref.kind === 'customer' || (ref.kind === 'contact' && ref.namespace === 'crm')) {
    candidates.push({
      app: 'crm',
      load: () => import('@omni-command/crm/src/federation/contextCardProvider'),
    })
  }
  if (ref.kind === 'customer' || ref.kind === 'task' || ref.kind === 'project') {
    candidates.push({
      app: 'projects',
      load: () => import('@omni-command/projects/src/federation/contextCardProvider'),
    })
  }

  for (const candidate of candidates) {
    try {
      const { makeContextCardProvider } = await candidate.load()
      const card = await makeContextCardProvider(tenantClient)(contextRef)
      if (card) return { type: 'sections', app: candidate.app, card }
    } catch (err) {
      if (import.meta.env.DEV) console.warn(`[peek] ${candidate.app} provider failed`, err)
    }
  }
  return { type: 'empty', reason: 'No surface could resolve this reference.' }
}
